import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { isValidPhone as isValidPhoneShared } from '../../constants/phone';

interface OnSiteContact {
  name: string;
  phone: string;
  shareWithProvider: boolean;
}

interface AdditionalContact {
  name?: string;
  phone?: string;
}

interface ContactSharingPreferencesProps {
  contact: OnSiteContact;
  onContactChange: (contact: OnSiteContact) => void;
  disabled?: boolean;
}

const ContactSharingPreferences: React.FC<ContactSharingPreferencesProps> = ({
  contact,
  onContactChange,
  disabled = false,
}) => {
  const { user } = useAuth();
  const [showAdditionalContact, setShowAdditionalContact] = useState(false);
  const [additionalContact, setAdditionalContact] = useState<AdditionalContact>({});

  // Get user's full name and phone as default contact
  const defaultContactName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  const defaultContactPhone = user?.phone || '';

  // Debug: Log user data
  console.log('ContactSharingPreferences - User:', user);
  console.log('ContactSharingPreferences - DefaultContactPhone:', defaultContactPhone);

  // Check if user object has phone field - if not, it might be stale JWT data
  const hasPhoneField = user && 'phone' in user;
  if (!hasPhoneField && user) {
    console.warn('User object is missing phone field - JWT token might be stale. Try logging out and back in.');
  }

  // Initialize contact with user profile if empty
  React.useEffect(() => {
    if (defaultContactName && !contact.name) {
      onContactChange({
        ...contact,
        name: defaultContactName,
        phone: defaultContactPhone,
      });
    }
  }, [defaultContactName, defaultContactPhone]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onContactChange({
      ...contact,
      name: e.target.value,
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Format phone number as user types
    let phone = e.target.value.replace(/\D/g, '');
    if (phone.length > 0) {
      if (phone.length <= 3) {
        phone = phone;
      } else if (phone.length <= 6) {
        phone = `${phone.slice(0, 3)}-${phone.slice(3)}`;
      } else {
        phone = `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
      }
    }

    onContactChange({
      ...contact,
      phone: e.target.value,
    });
  };

  const handleAdditionalNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalContact(prev => ({
      ...prev,
      name: e.target.value,
    }));
  };

  const handleAdditionalPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Format Malaysian phone number as user types (only while the digits
    // still look like a trunk-0 local number; SG numbers pass through raw)
    let phone = e.target.value.replace(/\D/g, '');
    if (phone.startsWith('0') || phone.startsWith('60')) {
      if (phone.length > 6) {
        phone = `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
      } else if (phone.length > 3) {
        phone = `${phone.slice(0, 3)}-${phone.slice(3)}`;
      }
    }

    setAdditionalContact(prev => ({
      ...prev,
      phone: e.target.value,
    }));
  };

  const handleSharingToggle = () => {
    onContactChange({
      ...contact,
      shareWithProvider: !contact.shareWithProvider,
    });
  };

  // Shared MY+SG validator (kept in sync with the server's phone module);
  // strip the display separators this component adds while typing
  const validatePhoneNumber = (phone: string): boolean =>
    isValidPhoneShared(phone.replace(/[\s()-]/g, ''));

  const isValidName = contact.name.trim().length >= 2;
  const isValidPhone = contact.phone.length === 0 || validatePhoneNumber(contact.phone);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            On-Site Contact Information
          </h3>
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-gray-500">Required for pickup coordination</span>
          </div>
        </div>

        {/* Default Contact (User Profile) */}
        {defaultContactName && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-blue-900">Primary Contact (Your Profile)</h4>
              <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Contact Name</label>
                <div className="flex items-center">
                  <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-900">{defaultContactName}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Phone Number</label>
                <div className="flex items-center">
                  <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-900">
                    {defaultContactPhone || 'No phone number in profile'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add More Button */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowAdditionalContact(!showAdditionalContact)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {showAdditionalContact ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              )}
            </svg>
            {showAdditionalContact ? 'Hide' : 'Add More'} Contact Information
          </button>
        </div>

        {/* Additional Contact Form (Collapsible) */}
        {showAdditionalContact && (
          <div className="border-t pt-4 animate-in slide-in-from-top-2 duration-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Additional Contact (Optional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Additional Name Field */}
              <div>
                <label htmlFor="additional-contact-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name (Optional)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="additional-contact-name"
                    value={additionalContact.name || ''}
                    onChange={handleAdditionalNameChange}
                    disabled={disabled}
                    placeholder="Enter additional contact name"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                  />
                </div>
              </div>

              {/* Additional Phone Field */}
              <div>
                <label htmlFor="additional-contact-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number (Optional)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    id="additional-contact-phone"
                    value={additionalContact.phone || ''}
                    onChange={handleAdditionalPhoneChange}
                    disabled={disabled}
                    placeholder="e.g., 012-345-6789"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                  />
                </div>
              </div>
            </div>

            {/* Use Additional Contact Button */}
            {(additionalContact.name || additionalContact.phone) && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    onContactChange({
                      ...contact,
                      name: additionalContact.name || contact.name,
                      phone: additionalContact.phone || contact.phone,
                    });
                    setAdditionalContact({});
                    setShowAdditionalContact(false);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Use This Contact
                </button>
              </div>
            )}
          </div>
        )}

        {/* Phone Sharing Toggle */}
        <div className="mt-6 border-t pt-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 pt-1">
              <input
                type="checkbox"
                id="share-phone"
                checked={contact.shareWithProvider}
                onChange={handleSharingToggle}
                disabled={disabled}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="share-phone" className="block">
                <span className="text-sm font-medium text-gray-900">
                  Share phone number with hearse provider
                </span>
                <span className="block text-sm text-gray-500 mt-1">
                  Allow the hearse provider to contact this person directly for pickup coordination and updates
                </span>
              </label>
            </div>
          </div>

          {/* Sharing Status */}
          {contact.shareWithProvider && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Phone Sharing Enabled
                  </h3>
                  <div className="mt-1 text-sm text-blue-700">
                    <p>The hearse provider will be able to:</p>
                    <ul className="mt-1 list-disc list-inside text-sm text-blue-700">
                      <li>Call upon arrival for pickup coordination</li>
                      <li>Send SMS updates about estimated arrival time</li>
                      <li>Contact for any access or location questions</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!contact.shareWithProvider && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    Phone Number Not Shared
                  </h3>
                  <p className="mt-1 text-sm text-amber-700">
                    The ambulance provider will not be able to contact this person directly.
                    You will need to coordinate pickup logistics yourself.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Validation Summary */}
      {contact.shareWithProvider && (!contact.phone || contact.phone.length === 0) && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Phone Number Required</h3>
              <p className="mt-1 text-sm text-red-700">
                {defaultContactPhone
                  ? 'Your profile phone number is available. Make sure the contact information above is correct.'
                  : 'Please add a phone number in your profile or provide additional contact information above.'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSharingPreferences;